/**
 * Gap-fills missing locale strings from bundled machine-translation JSON when MT
 * preview is active. Snapshots the real locale bundle before merging MT keys
 * because i18next `exists()` / `getResourceBundle()` include prior overlay merges;
 * re-baselines on CDN `loaded` so approved translations are not treated as gaps.
 */
import type { i18n as I18n } from "i18next";
import { get, has, set, unset } from "lodash-es";
import config from "@/config";
import { isPrimaryLanguage, PRIMARY_LANGUAGE } from "./primary-language";
import { isMachineTranslationPreviewActive } from "./machine-translation-preview";
import { DEFAULT_NAMESPACE } from "./translation-cdn";

const machineTranslationBundles = new Map<string, Record<string, unknown>>();
const machineFilledKeysByLocale = new Map<string, Set<string>>();
const machineFilledKeysCollectedForLocale = new Set<string>();
const baselineBundlesByLocale = new Map<string, Record<string, unknown>>();

function primaryLanguageSubtag(locale: string): string {
  return locale.split("-")[0]?.toLowerCase() ?? locale.toLowerCase();
}

function localesMatch(a: string, b: string): boolean {
  if (a.toLowerCase() === b.toLowerCase()) {
    return true;
  }
  return primaryLanguageSubtag(a) === primaryLanguageSubtag(b);
}

function trackedLocalesFor(locale: string): string[] {
  const matches = [...machineFilledKeysByLocale.keys()].filter((tracked) =>
    localesMatch(tracked, locale)
  );
  return matches.length > 0 ? matches : [locale];
}

function getBundleString(
  bundle: Record<string, unknown>,
  dotKey: string
): string | undefined {
  const value = get(bundle, dotKey);
  return typeof value === "string" ? value : undefined;
}

function isResourceBundle(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readNamespaceBundle(
  instance: I18n,
  locale: string
): Record<string, unknown> {
  const bundle: unknown = instance.getResourceBundle(locale, DEFAULT_NAMESPACE);
  return isResourceBundle(bundle) ? bundle : {};
}

function flattenLeafKeys(
  bundle: Record<string, unknown>,
  prefix = ""
): string[] {
  const keys: string[] = [];

  for (const [key, value] of Object.entries(bundle)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (isResourceBundle(value)) {
      keys.push(...flattenLeafKeys(value, path));
    } else {
      keys.push(path);
    }
  }

  return keys;
}

function stripOverlayKeysFromBundle(
  bundle: Record<string, unknown>,
  overlayKeys?: ReadonlySet<string>
): Record<string, unknown> {
  const clone = structuredClone(bundle);
  if (!overlayKeys?.size) {
    return clone;
  }

  for (const key of overlayKeys) {
    unset(clone, key);
  }

  return clone;
}

function buildNestedPatch(
  machineBundle: Record<string, unknown>,
  leafKeys: ReadonlySet<string>
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};

  for (const key of leafKeys) {
    const value = getBundleString(machineBundle, key);
    if (value !== undefined) {
      set(patch, key, value);
    }
  }

  return patch;
}

function captureBaselineBundle(instance: I18n, locale: string): void {
  if (baselineBundlesByLocale.has(locale)) {
    return;
  }

  const bundle = readNamespaceBundle(instance, locale);

  baselineBundlesByLocale.set(
    locale,
    config.translations.cdnEnabled
      ? structuredClone(bundle)
      : stripOverlayKeysFromBundle(
          bundle,
          machineFilledKeysByLocale.get(locale)
        )
  );
}

function isEnglishStubInRealBundle(
  instance: I18n,
  baselineBundle: Record<string, unknown>,
  key: string
): boolean {
  const localeValue = getBundleString(baselineBundle, key);
  if (localeValue === undefined) {
    return false;
  }

  const enBundle = readNamespaceBundle(instance, PRIMARY_LANGUAGE);
  const enValue = getBundleString(enBundle, key);

  return enValue !== undefined && localeValue === enValue;
}

function collectMachineFilledKeys(
  instance: I18n,
  locale: string,
  machineBundle: Record<string, unknown>
): Set<string> {
  captureBaselineBundle(instance, locale);
  const baseline = baselineBundlesByLocale.get(locale) ?? {};

  return new Set(
    flattenLeafKeys(machineBundle).filter((key) => {
      if (!has(baseline, key)) {
        return true;
      }

      return isEnglishStubInRealBundle(instance, baseline, key);
    })
  );
}

/** Whether a resolved key was gap-filled from the machine translation bundle. */
export function isMachineTranslatedKey(locale: string, key: string): boolean {
  for (const trackedLocale of trackedLocalesFor(locale)) {
    if (machineFilledKeysByLocale.get(trackedLocale)?.has(key)) {
      return true;
    }
  }

  return false;
}

export function getMachineTranslationBundlePath(locale: string): string {
  return `/locales/mt/${locale}/${DEFAULT_NAMESPACE}.json`;
}

export async function fetchMachineTranslationBundle(
  locale: string
): Promise<Record<string, unknown> | null> {
  try {
    const response = await fetch(getMachineTranslationBundlePath(locale), {
      cache: "no-store",
    });
    if (!response.ok) {
      return null;
    }

    const data: unknown = await response.json();
    return isResourceBundle(data) ? data : null;
  } catch {
    return null;
  }
}

function overlayMachineTranslationBundle(
  instance: I18n,
  locale: string,
  machineBundle: Record<string, unknown>
): void {
  if (
    !machineFilledKeysCollectedForLocale.has(locale) ||
    !baselineBundlesByLocale.has(locale)
  ) {
    machineFilledKeysByLocale.set(
      locale,
      collectMachineFilledKeys(instance, locale, machineBundle)
    );
    machineFilledKeysCollectedForLocale.add(locale);
  }

  const keysToApply =
    machineFilledKeysByLocale.get(locale) ?? new Set<string>();
  const patch = buildNestedPatch(machineBundle, keysToApply);
  if (Object.keys(patch).length === 0) {
    return;
  }

  machineTranslationBundles.set(locale, machineBundle);
  instance.addResourceBundle(locale, DEFAULT_NAMESPACE, patch, true, true);
}

function shouldApplyMachineTranslationOverlay(
  instance: I18n,
  locale: string
): boolean {
  return (
    isMachineTranslationPreviewActive() &&
    instance.language === locale &&
    !isPrimaryLanguage(locale)
  );
}

async function restoreLocaleTranslationBundle(
  instance: I18n,
  locale: string
): Promise<void> {
  try {
    if (instance.hasResourceBundle(locale, DEFAULT_NAMESPACE)) {
      instance.removeResourceBundle(locale, DEFAULT_NAMESPACE);
    }

    await instance.reloadResources(locale, DEFAULT_NAMESPACE);
  } catch {
    // Re-render without store `removed` bindings so stale MT text is not kept.
    instance.emit("languageChanged", instance.language);
  }
}

async function clearMachineTranslationOverlay(instance: I18n): Promise<void> {
  const localesToReload = [...machineTranslationBundles.keys()].filter(
    (locale) => !isPrimaryLanguage(locale)
  );
  machineTranslationBundles.clear();
  machineFilledKeysByLocale.clear();
  machineFilledKeysCollectedForLocale.clear();
  baselineBundlesByLocale.clear();

  await Promise.all(
    localesToReload.map((locale) =>
      restoreLocaleTranslationBundle(instance, locale)
    )
  );
}

async function performMachineTranslationOverlaySync(
  instance: I18n
): Promise<void> {
  const lng = instance.language;

  if (isMachineTranslationPreviewActive() && !isPrimaryLanguage(lng)) {
    const machineBundle = await fetchMachineTranslationBundle(lng);
    if (
      machineBundle !== null &&
      shouldApplyMachineTranslationOverlay(instance, lng)
    ) {
      overlayMachineTranslationBundle(instance, lng, machineBundle);
    }
    return;
  }

  await clearMachineTranslationOverlay(instance);
}

let overlaySyncChain: Promise<void> = Promise.resolve();

export async function syncMachineTranslationOverlay(
  instance: I18n
): Promise<void> {
  overlaySyncChain = overlaySyncChain
    .then(() => performMachineTranslationOverlaySync(instance))
    .catch(() => {
      // Callers intentionally fire-and-forget; never surface overlay sync errors.
    });

  return overlaySyncChain;
}

export function handleMachineTranslationLanguageChanged(instance: I18n): void {
  void syncMachineTranslationOverlay(instance);
}

/** Re-baseline gap-fill tracking once the real locale bundle has finished loading. */
export function handleMachineTranslationResourcesLoaded(
  instance: I18n,
  loaded: Record<string, Record<string, boolean>>
): void {
  if (!isMachineTranslationPreviewActive()) {
    return;
  }

  const localesNeedingRestore: string[] = [];

  for (const locale of Object.keys(loaded)) {
    if (isPrimaryLanguage(locale)) {
      continue;
    }

    if (
      config.translations.cdnEnabled &&
      machineTranslationBundles.has(locale)
    ) {
      localesNeedingRestore.push(locale);
    }

    machineFilledKeysCollectedForLocale.delete(locale);
    baselineBundlesByLocale.delete(locale);
  }

  for (const locale of localesNeedingRestore) {
    machineFilledKeysByLocale.delete(locale);
  }

  if (localesNeedingRestore.length === 0) {
    void syncMachineTranslationOverlay(instance);
    return;
  }

  overlaySyncChain = overlaySyncChain
    .then(async () => {
      for (const locale of localesNeedingRestore) {
        machineTranslationBundles.delete(locale);
      }

      await Promise.all(
        localesNeedingRestore.map((locale) =>
          restoreLocaleTranslationBundle(instance, locale)
        )
      );
      await performMachineTranslationOverlaySync(instance);
    })
    .catch(() => {
      // Callers intentionally fire-and-forget; never surface overlay sync errors.
    });
}
